import type { BIRequest } from '@/entities/bi'
import type { ColumnConfig } from '@/entities/data-export'
import type { FilterSchema } from '@/shared/components/filter/validation'
import type {
	SRT_ColumnDef,
	SRT_RowData,
} from '@/shared/components/master-table'
import type { TFunction } from 'i18next'
import type { WidgetPeriods } from '../types'

import { useMemo, useState } from 'react'
import { isMoneyMetric, useMoneyMetrics } from '@/pages/dashboard/lib/money'
import { useWidgetParams } from '@/pages/dashboard/lib/useWidgetParams'
import { useWidgetTable } from '@/pages/dashboard/lib/useWidgetTable'
import { any, isNil, orderBy, prop } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { comparePeriods, useBIAnalyticQuery } from '@/entities/bi'
import { useSelectedCurrency } from '@/entities/currency'

import NumberField from '@/shared/components/data-grid/fields/Number/TableCell'
import { ColumnDataType } from '@/shared/components/data-grid/types'
import {
	createSRTColumnHelper,
	MasterTable,
	SRT_TableToolbar,
} from '@/shared/components/master-table'
import { ValueType } from '@/shared/components/query-builder'
import { ampli } from '@/shared/lib/ampli'
import { formatMoney, formatNumber, NumberFormats } from '@/shared/tools/locale'

import MetricPerfomanceHeader from '../../MetricPerfomanceHeader'

//import getColumns from './columns'
type DataRow = {
	pricing_campaign_name: string
	sales_items: number
	revenue: number
	gross_profit: number
	gross_profit_margin: number
}
const metrics = [
	'sales_items',
	'revenue',
	'gross_profit',
	'gross_profit_margin',
]
const getRowId = prop('pricing_campaign_name')
const getReportSchema = <T extends SRT_RowData>(
	columns: SRT_ColumnDef<T, any>[]
): ColumnConfig[] => [
	...columns.map(({ id, header }) => ({
		key: id === 'name' ? 'pricing_campaign_name' : id!,
		type: id === 'name' ? ValueType.string : ValueType.number,
		name: header,
		custom_name: header,
		static_value: null,
		reference: null,
		enabled: true,
	})),
]
const getExportName = (name: string, periods: WidgetPeriods): string =>
	`${name} ${periods.main.start_date}-${periods.main.end_date} -> ${periods.compared.start_date}-${
		periods.compared.end_date
	}`
export default function MetricsByPricingCampaign({
	commonPeriods,
	commonFilters,
}: {
	commonPeriods: WidgetPeriods
	commonFilters: FilterSchema[]
}) {
	const { t } = useTranslation()

	const { params, onChangeParams } = useWidgetParams()
	const [localPeriods, setLocalPeriods] = useState<WidgetPeriods | null>(null)
	const defaultPeriods = commonPeriods
	const currency = useSelectedCurrency()
	const moneyMetrics = useMoneyMetrics()
	const commonQueryParams = {
		currency,
		label: 'MetricsByPricingCampaign',
		metrics,
		dimensions: ['pricing_campaign_name'] as BIRequest['dimensions'],
		date_aggregation: null,
		filters: [...commonFilters, ...(params.filters ?? [])],
	}
	const mainPeriodQuery = useBIAnalyticQuery({
		...(localPeriods ? localPeriods?.main : commonPeriods.main),
		...commonQueryParams,
	})
	const comparisonPeriodQuery = useBIAnalyticQuery({
		...(localPeriods ? localPeriods?.compared : commonPeriods.compared),
		...commonQueryParams,
	})
	const isLoading = any(prop('isLoading'), [
		mainPeriodQuery,
		comparisonPeriodQuery,
	])
	const columns = useMemo(
		() => getColumns(t, currency, moneyMetrics),
		[t, currency, moneyMetrics]
	)
	const fields = getReportSchema(columns)
	const rows = comparePeriods(
		orderBy([0], ['asc'], mainPeriodQuery?.data?.data),
		orderBy([0], ['asc'], comparisonPeriodQuery?.data?.data),
		mainPeriodQuery?.data?.meta,
		getRowId
	) as DataRow[]
	const name = getExportName(
		t('widget.metric_by_pc.title'),
		localPeriods || commonPeriods
	)
	const handleFilterChange = (filters: FilterSchema[]) => {
		ampli.dashboardBiApplyFilterMetricsByPricingCampaign({
			'filter by': filters.map(prop('name')).join(','),
		})
		onChangeParams({ ...params, filters })
	}
	const table = useWidgetTable<DataRow>({
		rows,
		columns,
		isLoading,
		getRowId,
		getRowHeight: () => 30,
	})

	return (
		<div className='flex-auto space-y-3'>
			<h4>{t('widget.metric_by_pc.title')}</h4>
			<div className='flex w-full items-center gap-2'>
				<SRT_TableToolbar table={table} />
				<MetricPerfomanceHeader
					data={{ fields, rows, name, kind: 'metric_by_pc' }}
					params={params}
					commonFilters={commonFilters}
					onFilterChange={handleFilterChange}
					periods={localPeriods || commonPeriods}
					setPeriods={setLocalPeriods}
					defaultPeriods={defaultPeriods}
				/>
			</div>
			<div className='border-base relative flex h-96 max-h-96 flex-1 flex-col overflow-hidden rounded-lg border border-solid'>
				<MasterTable table={table} />
			</div>
		</div>
	)
}

const columnHelper = createSRTColumnHelper<DataRow>()

const getTextColor = (value: number) => {
	if (value > 0) {
		return 'text-success'
	}
	if (value < 0) {
		return 'text-destructive'
	}
}

function getColumns(
	t: TFunction,
	currency: string | undefined,
	moneyMetrics: string[]
) {
	return [
		columnHelper.accessor((row) => row?.pricing_campaign_name || 'n/a', {
			id: 'name',
			header: t('pricing_campaign', { defaultValue: 'Pricing Campaign' }),
			enableSorting: true,
			size: 290,
			meta: {
				label: t('pricing_campaign', { defaultValue: 'Pricing Campaign' }),
				placeholder: t('general_search', { defaultValue: 'Search by brand' }),
				variant: 'text',
			},
		}),
		...metrics.reduce(
			(cols, metric) => {
				cols.push(
					columnHelper.accessor(prop(metric) as (row: DataRow) => number, {
						id: metric,
						header: t(metric),
						Cell: ({ cellValue }) =>
							isMoneyMetric(metric, moneyMetrics) && !isNil(cellValue) ? (
								<div className='cell-data-number'>
									{formatMoney(cellValue, currency, NumberFormats.Fixed)}
								</div>
							) : (
								<NumberField
									value={cellValue}
									schema={{ type: ColumnDataType.Float }}
								/>
							),
						size: 125,
					})
				)

				cols.push(
					columnHelper.accessor(
						prop(metric + '_difference') as (row: DataRow) => number,
						{
							id: metric + '_difference',
							header: t('field_schema_delta_abs', {
								ns: 'fields',
								defaultValue: 'Δ',
							}),
							Cell: ({ cellValue }) => (
								<div className='flex items-center justify-end font-mono'>
									<p className={getTextColor(cellValue!)}>
										{isNil(cellValue) && cellValue != 0
											? '-'
											: isMoneyMetric(metric, moneyMetrics)
												? formatMoney(cellValue, currency, NumberFormats.Fixed)
												: formatNumber(cellValue)}
									</p>
								</div>
							),
							size: 125,
						}
					)
				)
				cols.push(
					columnHelper.accessor(
						prop(metric + '_difference_percent') as (row: DataRow) => number,
						{
							id: metric + '_difference_percent',
							header: t('field_schema_delta_pp', {
								ns: 'fields',
								defaultValue: 'Δ %',
							}),
							Cell: ({ cellValue }) => (
								<div className='flex items-center justify-end font-mono'>
									<p className={getTextColor(cellValue!)}>
										{isNil(cellValue) && cellValue != 0
											? '-'
											: formatNumber(cellValue, NumberFormats.Percent)}
									</p>
								</div>
							),
							size: 125,
						}
					)
				)

				return cols
			},
			[] as SRT_ColumnDef<DataRow, number>[]
		),
	]
}
